"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { type components, type Page } from "@theta/api-client";
import { thetaApi, dataOf, pageOf } from "@/lib/api/theta";
import { invalidateGroupDocuments } from "@/lib/hooks/useGroupPage";
import { retryUnlessRefused } from "./queryRetry";

/**
 * MEMBERSHIPS, ON /api/v1 — spec §2.4
 *
 * `lib/api/memberships.ts` is gone. What changed on the way, beyond the URLs:
 *
 *  - THE MEMBER ROW CARRIES `can`. `approve`, `reject`, `ban`, `expel`,
 *    `changeRole` and `readApplicationAnswers` come from the predicates the
 *    routes enforce (D11), so the queue draws the buttons the server will
 *    honour instead of re-deriving them from a role the card no longer ships.
 *  - JOIN IS A PUT ON A SINGLETON (D10b), so a double-tap is one membership
 *    and needs no idempotency key. LEAVE is the same address with DELETE, and
 *    `can.leave` is the server's own verdict on whether there is one to offer
 *    — the last admin of a group cannot leave it (`409 lastAdmin`), which the
 *    legacy route let happen and stranded the group.
 *  - `PUT …/role` became `PATCH …/members/{userId}`; ban and temp-ban merged
 *    into one call whose `untilDays` is optional.
 *  - `reject` no longer DELETES the row. It becomes `expelled` with a reason,
 *    which is a real membership to return and an audit trail — the legacy
 *    handler deleted it and answered `{success: true}`.
 *  - THE REASON IS REQUIRED on expel (§2.4). An expulsion with no stated
 *    reason is the support ticket nobody can answer.
 *  - `meta.total` is gone (D4). The member grid counts nothing; the group
 *    document's `memberCount` is the number, from the server.
 */

type Schemas = components["schemas"];

export type Membership = Schemas["Membership"];
type MemberStatus = NonNullable<Schemas["Membership"]>["status"] | "all";
type MemberRole = Schemas["Membership"]["role"];

/** Every filter, `limit` included — see the note on `groupsKey`. */
function membersKey(ref: string, filters: MemberFilters = {}): (string | number | boolean)[] {
  return [
    "v1",
    "members",
    ref,
    filters.status ?? "",
    filters.q ?? "",
    filters.includeInherited ?? false,
    filters.limit ?? 0,
  ];
}

interface MemberFilters {
  /** Anything but `active` is MODERATOR-gated and 404s below that (D7). */
  status?: MemberStatus | undefined;
  q?: string | undefined;
  /** Prepends the parent group's admins, on the FIRST page only. */
  includeInherited?: boolean | undefined;
  limit?: number | undefined;
}

export function useV1Members(
  ref: string,
  filters: MemberFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: membersKey(ref, filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      thetaApi.http
        .GET("/groups/{ref}/members", {
          params: {
            path: { ref },
            query: {
              ...(filters.status ? { status: filters.status } : {}),
              ...(filters.q ? { q: filters.q } : {}),
              ...(filters.includeInherited ? { includeInherited: "true" as const } : {}),
              ...(filters.limit ? { limit: filters.limit } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        })
        .then(pageOf<Membership>),
    getNextPageParam: (last: Page<Membership>) => (last.hasMore ? last.nextCursor : undefined),
    enabled: ref.length > 0 && (options.enabled ?? true),
    retry: retryUnlessRefused,
  });
}

export function flatMembers(data: { pages: Page<Membership>[] } | undefined): Membership[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

function invalidateMembers(queryClient: QueryClient, ref: string) {
  void queryClient.invalidateQueries({ queryKey: ["v1", "members", ref] });
  // `pendingCounts.requests` — the admin sidebar's "Ventende anmodninger"
  // badge — is on the group document, so approving or rejecting has to make
  // it be read again. Every cached spelling of the ref, not just the one
  // this hook was handed: see `invalidateGroupDocuments` (OSO-432).
  invalidateGroupDocuments(queryClient);
  void queryClient.invalidateQueries({ queryKey: ["v1", "groups"] });
}

/**
 * Join, or apply. A PUT on the viewer's own membership: a second identical
 * call has nothing to duplicate and answers with the same row (D10b).
 *
 * A group that requires approval — every non-public one does — produces a
 * PENDING membership, which grants nothing. Every question the group asks must
 * be answered or the server refuses with the field named.
 */
export function useJoinGroup(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (applicationAnswers?: Record<string, string>) =>
      thetaApi.http
        .PUT("/groups/{ref}/membership", {
          params: { path: { ref } },
          body: applicationAnswers ? { applicationAnswers } : {},
        })
        .then(dataOf<Membership>),
    onSuccess: () => invalidateMembers(queryClient, ref),
  });
}

/**
 * Leave, or withdraw a pending application — the same address, DELETE.
 *
 * `GroupHeader` is the caller (OSO-421 — for the whole port there was none,
 * and the only e2e coverage clicked the button "if visible", so nothing
 * failed while the button did not exist).
 *
 * WHAT GATES IT IS `can.leave`, AND THAT IS NOT THE LAST-ADMIN RULE.
 * `can.leave` is "this viewer holds an active membership"
 * (`groupCapabilities`, src/modules/groups/access.ts). The SOLE ADMIN of a
 * group holds one, sees the control, and is refused by `409 lastAdmin` — the
 * guard G11 put on role changes and, until the port, never on this route, so
 * an admin could walk out of their own group and strand it. Counting admins
 * in the client to hide the button would be that rule restated where it
 * cannot be enforced; the caller shows the server's own sentence instead.
 */
export function useLeaveGroup(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      thetaApi.http
        .DELETE("/groups/{ref}/membership", { params: { path: { ref } } })
        .then(dataOf<null>),
    onSuccess: () => invalidateMembers(queryClient, ref),
  });
}

/**
 * The viewer's own settings for a group: favourite, flag notifications, and
 * `hasVisited`.
 *
 * `hasVisited: true` is WHAT CLEARS THE UNREAD BADGE. §1.6 forbids a GET with
 * a side effect, so the stamp the old by-slug read did on the way past is an
 * explicit act — which is why opening a group posts this.
 */
export function useUpdateMembership(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["UpdateMembershipBodyInput"]) =>
      thetaApi.http
        .PATCH("/groups/{ref}/membership", { params: { path: { ref } }, body })
        .then(dataOf<Membership>),
    onSuccess: () => invalidateMembers(queryClient, ref),
  });
}

/** The moderation queue's four verbs, and the admin-only role change. */
export function useMemberActions(ref: string) {
  const queryClient = useQueryClient();
  const settled = { onSuccess: () => invalidateMembers(queryClient, ref) };

  const approve = useMutation({
    mutationFn: (userId: number) =>
      thetaApi.http
        .POST("/groups/{ref}/members/{userId}/approve", { params: { path: { ref, userId } } })
        .then(dataOf<Membership>),
    ...settled,
  });

  const reject = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason?: string }) =>
      thetaApi.http
        .POST("/groups/{ref}/members/{userId}/reject", {
          params: { path: { ref, userId } },
          body: { reason: reason ?? null },
        })
        .then(dataOf<Membership>),
    ...settled,
  });

  const ban = useMutation({
    mutationFn: ({ userId, reason, untilDays }: { userId: number; reason: string; untilDays?: number | null }) =>
      thetaApi.http
        .POST("/groups/{ref}/members/{userId}/ban", {
          params: { path: { ref, userId } },
          body: { reason, untilDays: untilDays ?? null },
        })
        .then(dataOf<Membership>),
    ...settled,
  });

  const expel = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason: string }) =>
      thetaApi.http
        .POST("/groups/{ref}/members/{userId}/expel", {
          params: { path: { ref, userId } },
          body: { reason },
        })
        .then(dataOf<Membership>),
    ...settled,
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: MemberRole }) =>
      thetaApi.http
        .PATCH("/groups/{ref}/members/{userId}", {
          params: { path: { ref, userId } },
          body: { role },
        })
        .then(dataOf<Membership>),
    ...settled,
  });

  return { approve, reject, ban, expel, changeRole };
}

/**
 * The favourite star, for a list that shows many groups at once.
 *
 * `useUpdateMembership` is bound to one ref; the sidebars star whichever row
 * was clicked, so the ref travels with the call. It is the SAME route and the
 * same `isFavourite` field — a value, never a toggle.
 */
export function useSetFavourite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, isFavourite }: { ref: string; isFavourite: boolean }) =>
      thetaApi.http
        .PATCH("/groups/{ref}/membership", {
          params: { path: { ref } },
          body: { isFavourite },
        })
        .then(dataOf<Membership>),
    onSuccess: (_row, { ref }) => invalidateMembers(queryClient, ref),
  });
}
